import * as React from 'react'
import { useMinimumWageQuery } from '../useMinimumWageQuery'
import { MinimalWagePageProps } from '../types'
import { useMinimumWageMutation } from '../useMinimumWageMutation'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { MinimumWageCountryLayout } from '../components/MinimumWageCountryLayout'
import { FieldName, MinimumWageOptionsSection } from '../containers/MinimumWageOptionsSection'
import { CalculationStartDateSection } from '../containers/CalculationStartDateSection'
import { MinimumWagePolishOptionsSection } from '../containers/MinimumWagePolishOptionsSection'
import { CzechRepublicMinimumWageExpandableItemSection } from '../containers/CzechRepublicMinimumWageExpandableItemSection'
import { CzechRepublicMinimumWageConfigurationModel } from '@/app/models/configuration/minimumWage/czechRepublicMinimumWageConfigurationModel'

import { PreLoader } from '../components/PreLoader'
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils'

const defaultFields: FieldName[] = [
  'printCalculatedHours',
  'calculateCabotageAndCrossTrade',
]

const allowedCurrenciesInner = ['CZK']

const country = 'czechRepublic'

export const CzechRepublicMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
  const { companyId } = match.params
  const q = useMinimumWageQuery(companyId)
  const up = useMinimumWageMutation(companyId)
  const { t } = useLocalize()

  const [state, setState] = React.useState<CzechRepublicMinimumWageConfigurationModel | null>(null)

  React.useEffect(() => {
    setState(null)
  }, [companyId])

  React.useEffect(() => {
    if (q.data) {
      setState(structuredClone(q.data.czechRepublicConfiguration))
    }
  }, [q.data])

  const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('czechRepublicConfiguration', t), [t])
  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    configuration: t('configuration.minimumWages.options'),
    expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
  }), [t])

  if (!state || !q.data) {
    return <PreLoader />
  }

  const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.czechRepublicConfiguration)

  return (
    <MinimumWageCountryLayout
      selectedCountryValue={country}
      isDirty={isDirty}
      handleSave={() => up.mutateAsync({ ...q.data, czechRepublicConfiguration: state })}
      handleCancel={() => setState(q.data.czechRepublicConfiguration)}
      dataTestId="czech-republic-minimum-wage-settings-page"
      isLoading={q.isFetching || up.isPending}
      diffBefore={q.data.czechRepublicConfiguration as unknown as Record<string, unknown>}
      diffAfter={state as unknown as Record<string, unknown>}
      diffLabels={diffLabels}
      diffRenderers={diffRenderers}
    >
      <CalculationStartDateSection state={state} setState={setState} />

      <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
        <CzechRepublicMinimumWageExpandableItemSection
          state={state}
          setState={setState}
          allowedCurrencies={allowedCurrenciesInner}
        />

        <MinimumWageOptionsSection
          className="tw-my-5"
          loading={false}
          fields={defaultFields}
          translateKey="genericConfiguration"
          state={state}
          accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
          setState={setState}
        />

        <MinimumWagePolishOptionsSection
          className="tw-my-5"
          loading={false}
          state={state}
          setState={setState}
        />
      </div>

      <div style={{ minHeight: '90px' }} />
    </MinimumWageCountryLayout>
  )
}
